import { useState } from "react";
import { usePageTitle } from "@/hooks/usePageTitle";
import { ArrowRight, Coins, Gift, Plus, ShoppingCart, TrendingUp, Zap } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { startOfMonth, subWeeks, startOfWeek, endOfWeek, format, min } from "date-fns";
import { SidebarProvider } from "@/components/ui/sidebar";
import { DashboardTopNav } from "@/components/layout/DashboardTopNav";
import { TeacherSidebar } from "@/components/teacher/dashboard/TeacherSidebar";
import { useTeacherProfile } from "@/contexts/TeacherProfileContext";
import { useAuth } from "@/hooks/use-auth-compat";
import { useCurrentUser } from "@/hooks/use-current-user";
import { fetchPublicSubscriptionPlans } from "@/services/teacherSubscriptionService";
import {
  fetchTeacherCoinSpends,
  fetchTeacherCoinSummary,
  fetchTeacherRegistrationGrant,
} from "@/services/teacherProfileService";
import { fetchCoinPackages, fetchPurchaseOrders, type PurchaseOrderHistory } from "@/services/coinPurchaseService";
import { CoinSpendTable } from "@/components/coins/CoinSpendTable";
import { PurchaseReceiptModal } from "@/components/coins/PurchaseReceiptModal";
import { Skeleton } from "@/components/ui/skeleton";
import { currencySymbol } from "@/lib/currency";
import { packAmountMinorFor } from "@/lib/paymentAmounts";
import { PricesRetryAlert } from "@/components/coins/PricesRetryAlert";
import { cn } from "@/lib/utils";

function formatCoinDate(iso: string): string {
  return new Date(iso).toLocaleDateString([], { year: "numeric", month: "short", day: "numeric" });
}

function buildWeeklyBuckets(rows: { createdAt: string; amount: number }[]) {
  return Array.from({ length: 4 }, (_, i) => {
    const weekStart = startOfWeek(subWeeks(new Date(), 3 - i), { weekStartsOn: 1 });
    const weekEnd = endOfWeek(weekStart, { weekStartsOn: 1 });
    const spent = rows
      .filter((r) => r.amount < 0 && new Date(r.createdAt) >= weekStart && new Date(r.createdAt) <= weekEnd)
      .reduce((sum, r) => sum + Math.abs(r.amount), 0);
    return { label: format(weekStart, "MMM d"), spent };
  });
}

export default function TeacherCoinsPage() {
  usePageTitle('BMC Coins | BookMyCoaching');
  const { teacherProfile, profileResponse, hasActiveVersion } = useTeacherProfile();
  const auth = useAuth();
  const navigate = useNavigate();
  const token = auth.user?.access_token ?? "";

  const { user } = useCurrentUser();
  const teacherRoleCreatedDate = user?.roles.find((r) => r.role === "TEACHER")?.createdDate;
  const registrationDate = teacherRoleCreatedDate ? new Date(teacherRoleCreatedDate) : undefined;

  // Same server-resolved currency as /pricing and the top-up pages (ADR-151). This page shows what
  // coins are WORTH, so quoting it in a different currency than the page that sells them read as a
  // different price for the same thing -- it showed "$0.35 at base rate" while every purchase
  // surface quoted rupees.
  //
  // /marketplace/geo-defaults, used here before, falls back to DEFAULT_CURRENCY = "USD"; the
  // resolver behind /pricing falls back to TEACHER_DEFAULT_CURRENCY = "INR", and is what the
  // forced-currency switch acts on.
  const {
    data: plansForCurrency,
    isError: plansError,
    refetch: refetchPlans,
  } = useQuery({
    queryKey: ["public", "teacher-plans"],
    queryFn: fetchPublicSubscriptionPlans,
    staleTime: 5 * 60 * 1000,
  });
  // SCRUM-365: the plans currency exactly as returned -- no INR fallback (D2).
  const rawCurrency = plansForCurrency?.currency ?? null;
  const sym = currencySymbol(rawCurrency);
  const showPlansError = plansError;

  const [receiptOrder, setReceiptOrder] = useState<PurchaseOrderHistory | null>(null);
  const [pageIndex, setPageIndex] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateFrom, setDateFrom] = useState<Date | undefined>(undefined);
  const [dateTo, setDateTo] = useState<Date | undefined>(undefined);

  const { data: registrationGrant } = useQuery({
    queryKey: ["teacherRegistrationGrant"],
    queryFn: () => fetchTeacherRegistrationGrant(token),
    enabled: !!token,
  });

  const { data: coinSummary, isLoading: coinSummaryLoading } = useQuery({
    queryKey: ["teacherCoinSummary"],
    queryFn: () => fetchTeacherCoinSummary(token),
    enabled: !!token,
  });

  const analyticsFrom = min([startOfMonth(new Date()), subWeeks(new Date(), 4)]);
  const analyticsMonthKey = format(new Date(), "yyyy-MM");
  const { data: recentSpends } = useQuery({
    queryKey: ["teacherRecentSpends", analyticsMonthKey],
    queryFn: () =>
      fetchTeacherCoinSpends(token, { page: 0, size: 200, dateFrom: analyticsFrom }),
    enabled: !!token,
    staleTime: 2 * 60 * 1000,
  });

  const { data: packages } = useQuery({
    queryKey: ["coinPackages"],
    queryFn: fetchCoinPackages,
    staleTime: 5 * 60 * 1000,
  });

  const { data: purchaseOrders } = useQuery({
    queryKey: ["teacherPurchaseOrders"],
    queryFn: () => fetchPurchaseOrders(token),
    enabled: !!token,
    staleTime: 2 * 60 * 1000,
  });

  const {
    data: coinSpendsPage,
    isLoading: coinSpendsLoading,
    isError: coinSpendsError,
  } = useQuery({
    queryKey: ["teacherCoinSpends", { page: pageIndex, name: searchTerm, dateFrom, dateTo }],
    queryFn: () =>
      fetchTeacherCoinSpends(token, { page: pageIndex, size: 10, name: searchTerm || undefined, dateFrom, dateTo }),
    enabled: !!token,
  });

  const teacherName = teacherProfile?.fullName || "";
  const teacherAvatar = teacherProfile?.profileImage || "";
  const teacherSubjects: string[] = (teacherProfile?.subjects || [])
    .map((s: any) => typeof s === "string" ? s : s?.subjectName || s?.subject?.name || s?.name || "")
    .filter(Boolean);

  // coinBalance: prefer the summary endpoint (also gives chargeAmount), fall back to profile-level field
  const coinBalance = coinSummary?.balance ?? profileResponse?.profile?.coinBalance ?? null;
  const chargeAmount = coinSummary?.chargeAmount ?? 30;
  const repliesRemaining = coinBalance != null ? Math.floor(coinBalance / chargeAmount) : null;

  const recentRows = recentSpends?.content ?? [];
  const thisMonthStart = startOfMonth(new Date());
  const monthlyCharges = recentRows
    .filter((r) => r.amount < 0 && new Date(r.createdAt) >= thisMonthStart)
    .reduce((sum, r) => sum + Math.abs(r.amount), 0);
  const monthlyReplies = chargeAmount > 0 ? Math.floor(monthlyCharges / chargeAmount) : 0;

  const weeklyBuckets = buildWeeklyBuckets(recentRows);
  const maxWeeklySpend = Math.max(...weeklyBuckets.map((b) => b.spent), 1);

  const basePkg = packages?.[0];
  // No figure (null) until the backend currency is known and supported (D2).
  const baseMinorOn = basePkg ? packAmountMinorFor(basePkg, rawCurrency) : null;
  const basePricePerCoin = basePkg && baseMinorOn !== null ? baseMinorOn / 100 / basePkg.coins : null;
  const perReplyCost = basePricePerCoin != null ? basePricePerCoin * chargeAmount : null;

  const suggestedPkg = packages?.find((p) => p.coins >= monthlyCharges * 2) ?? packages?.[packages.length - 1];
  const suggestedMonthsCovered = suggestedPkg && monthlyCharges > 0
    ? Math.round(suggestedPkg.coins / monthlyCharges)
    : null;

  return (
    <div className="min-h-screen bg-background teacher-theme">
      <SidebarProvider>
        <DashboardTopNav role="teacher" />
        <div className="min-h-screen flex w-full pt-14">
          <TeacherSidebar teacherName={teacherName} teacherAvatar={teacherAvatar} subjects={teacherSubjects} />

          <div className="flex-1 flex flex-col min-w-0">
            <main className="flex-1 overflow-auto bg-slate-50">
              <div className="px-4 sm:px-6 pt-5 pb-4 border-b border-slate-100">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <h1 className="text-2xl font-bold text-slate-900">BMC Coins</h1>
                    <p className="text-sm text-slate-500 mt-0.5">Your balance &amp; transaction history</p>
                  </div>
                  {hasActiveVersion && (
                    <Link
                      to="/teacher-buy-coins"
                      className="inline-flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold px-3.5 py-2 rounded-xl transition-colors"
                    >
                      <Plus className="w-3.5 h-3.5" /> Top Up
                    </Link>
                  )}
                </div>
              </div>

              <div className="px-4 sm:px-6 py-5 space-y-3">
                {/* Primary stat tiles */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
                  {/* Available */}
                  <div className="col-span-2 sm:col-span-1 bg-gradient-to-br from-indigo-600 to-violet-700 rounded-xl p-4 text-white">
                    <div className="flex items-center gap-2 mb-2">
                      <Coins className="w-4 h-4 text-white/70" />
                      <span className="text-xs font-semibold text-white/70">Available</span>
                    </div>
                    {coinSummaryLoading ? (
                      <>
                        <Skeleton className="h-8 w-20 bg-white/20" />
                        <Skeleton className="h-3 w-32 bg-white/20 mt-2" />
                      </>
                    ) : coinBalance != null ? (
                      <>
                        <p className="text-3xl font-black leading-none">{coinBalance.toLocaleString()}</p>
                        <p className="text-xs text-white/60 mt-1">
                          {repliesRemaining != null
                            ? `= ${repliesRemaining} ${repliesRemaining === 1 ? "reply" : "replies"} remaining`
                            : "BMC Coins ready to use"}
                        </p>
                        {perReplyCost != null && (
                          <p className="text-xs text-white/40 mt-0.5">
                            each reply ≈ {sym}{perReplyCost < 1 ? perReplyCost.toFixed(2) : Math.round(perReplyCost)}
                          </p>
                        )}
                      </>
                    ) : (
                      <>
                        <p className="text-lg font-bold leading-none">No balance yet</p>
                        <p className="text-xs text-white/60 mt-1">Coins granted on registration</p>
                      </>
                    )}
                  </div>

                  {/* Per reply cost */}
                  <div className="bg-slate-50 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <ArrowRight className="w-4 h-4 text-slate-500" />
                      <span className="text-xs font-bold text-slate-500">Per Reply</span>
                    </div>
                    <p className="text-3xl font-black leading-none text-slate-800">{chargeAmount}</p>
                    <p className="text-xs text-slate-500 mt-1">coins per student reply</p>
                    {showPlansError ? (
                      <PricesRetryAlert onRetry={() => void refetchPlans()} className="mt-2 p-3" />
                    ) : perReplyCost != null && (
                      <p className="text-xs text-slate-400 mt-1">
                        ≈ {sym}{perReplyCost < 1 ? perReplyCost.toFixed(2) : Math.round(perReplyCost)} at base rate
                      </p>
                    )}
                  </div>

                  {/* Signup bonus */}
                  <div className="bg-amber-50 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Gift className="w-4 h-4 text-amber-500" />
                      <span className="text-xs font-bold text-amber-700">Signup Bonus</span>
                    </div>
                    {registrationGrant ? (
                      <>
                        <p className="text-3xl font-black leading-none text-slate-800">+{registrationGrant.amount.toLocaleString()}</p>
                        <p className="text-xs text-amber-600 mt-1">on {formatCoinDate(registrationGrant.createdAt)}</p>
                      </>
                    ) : (
                      <>
                        <p className="text-3xl font-black leading-none text-slate-300">—</p>
                        <p className="text-xs text-slate-400 mt-1">one-time on registration</p>
                      </>
                    )}
                  </div>
                </div>

                {/* New-user empty state */}
                {!coinSpendsLoading && recentRows.length === 0 && coinBalance != null && coinBalance > 0 && hasActiveVersion && (
                  <div className="bg-indigo-50 border border-indigo-100 rounded-xl px-4 py-4 mb-3 flex items-center justify-between gap-4">
                    <div>
                      <p className="text-sm font-bold text-indigo-800">You have {coinBalance.toLocaleString()} coins ready to use</p>
                      <p className="text-xs text-indigo-500 mt-0.5">
                        When students send you an inquiry, reply to them — each reply costs {chargeAmount} coins.
                      </p>
                    </div>
                    <Link
                      to="/teacher-inquiries"
                      className="inline-flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold px-3.5 py-2 rounded-xl transition-colors shrink-0"
                    >
                      View Inquiries <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                  </div>
                )}

                {/* Activity tiles */}
                {recentRows.length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
                    {/* This month */}
                    <div className="bg-indigo-50 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <ShoppingCart className="w-4 h-4 text-indigo-500" />
                        <span className="text-xs font-bold text-indigo-600">This Month</span>
                      </div>
                      <p className="text-2xl font-black leading-none text-slate-800">{monthlyCharges.toLocaleString()}</p>
                      <p className="text-xs text-indigo-400 mt-1">
                        coins spent · {monthlyReplies} {monthlyReplies === 1 ? "reply" : "replies"}
                      </p>
                    </div>

                    {/* Weekly trend */}
                    <div className="bg-violet-50 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <TrendingUp className="w-4 h-4 text-violet-500" />
                        <span className="text-xs font-bold text-violet-600">4-Week Trend</span>
                      </div>
                      <div className="flex items-end gap-1.5 h-10">
                        {weeklyBuckets.map((bucket, i) => (
                          <div key={i} className="flex-1 flex flex-col items-center gap-1">
                            <div
                              className={cn(
                                "w-full rounded-t-sm transition-all",
                                bucket.spent > 0 ? "bg-violet-400" : "bg-violet-100",
                              )}
                              style={{ height: `${Math.max((bucket.spent / maxWeeklySpend) * 36, bucket.spent > 0 ? 6 : 4)}px` }}
                            />
                          </div>
                        ))}
                      </div>
                      <div className="flex gap-1.5 mt-1">
                        {weeklyBuckets.map((bucket, i) => (
                          <p key={i} className="flex-1 text-center text-xs text-slate-400 truncate">{bucket.label}</p>
                        ))}
                      </div>
                    </div>

                    {/* Top-up suggestion */}
                    <div className="col-span-2 sm:col-span-1 bg-emerald-50 rounded-xl p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 mb-2">
                          <Zap className="w-4 h-4 text-emerald-500" />
                          <span className="text-xs font-bold text-emerald-700">Suggested Pack</span>
                        </div>
                        {suggestedPkg && monthlyCharges > 0 && (
                          <Link
                            to="/teacher-buy-coins"
                            className="sm:hidden inline-flex items-center gap-1 text-xs font-bold text-emerald-700 hover:text-emerald-800 mb-2"
                          >
                            View <ArrowRight className="w-3 h-3" />
                          </Link>
                        )}
                      </div>
                      {suggestedPkg && monthlyCharges > 0 ? (
                        <>
                          <p className="text-2xl font-black leading-none text-slate-800">{suggestedPkg.coins.toLocaleString()}</p>
                          <p className="text-xs text-emerald-600 mt-1">
                            coins{suggestedMonthsCovered && suggestedMonthsCovered > 0
                              ? ` · ~${suggestedMonthsCovered} months at your rate`
                              : ""}
                          </p>
                          <Link
                            to="/teacher-buy-coins"
                            className="hidden sm:inline-flex items-center gap-1 text-xs font-bold text-emerald-700 hover:text-emerald-800 mt-2"
                          >
                            View pack <ArrowRight className="w-3 h-3" />
                          </Link>
                        </>
                      ) : (
                        <>
                          <p className="text-sm font-semibold text-slate-400 mt-1">No activity yet</p>
                          <p className="text-xs text-slate-400 mt-0.5">Suggestion based on your spending</p>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* Low balance nudge */}
                {!coinSummaryLoading && coinBalance != null && coinBalance < chargeAmount && (
                  <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-3">
                    <p className="text-xs font-medium text-amber-700">
                      You have fewer than {chargeAmount} coins — not enough to reply to a student inquiry.
                    </p>
                    {hasActiveVersion && (
                      <Link to="/teacher-buy-coins" className="inline-flex items-center gap-1 text-xs font-bold text-amber-700 hover:text-amber-900 shrink-0 ml-3">
                        Top up <ArrowRight className="w-3 h-3" />
                      </Link>
                    )}
                  </div>
                )}

                {/* Profile not approved notice */}
                {!hasActiveVersion && (
                  <div className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 mb-3">
                    <p className="text-xs text-slate-500">
                      Complete your profile review to unlock coin top-ups and start replying to students.
                    </p>
                  </div>
                )}

                {/* Transaction history */}
                {/* Purchase history — copyable payment IDs for disputes / refunds */}
                {purchaseOrders && purchaseOrders.length > 0 && (
                  <div className="mb-4">
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Purchase History</p>
                    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                      {purchaseOrders.map((order, i) => {
                        const sym = currencySymbol(order.currency as "INR" | "USD");
                        const amount = order.amount / 100;
                        const fmtAmount = `${sym}${Number.isInteger(amount) ? amount.toLocaleString() : amount.toFixed(2)}`;
                        const date = new Date(order.paidAt ?? order.createdAt).toLocaleDateString([], {
                          year: "numeric", month: "short", day: "numeric",
                        });
                        const statusColor = order.status === "PAID"
                          ? "text-emerald-600 bg-emerald-50"
                          : order.status === "DISPUTED"
                          ? "text-amber-600 bg-amber-50"
                          : "text-slate-500 bg-slate-100";
                        return (
                          <button
                            key={order.orderId}
                            onClick={() => setReceiptOrder(order)}
                            className={cn(
                              "w-full text-left flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors",
                              i > 0 && "border-t border-slate-100",
                            )}
                          >
                            <div className="min-w-0">
                              <p className="text-sm font-bold text-slate-800 truncate">
                                {order.coins.toLocaleString()} coins · {fmtAmount}
                              </p>
                              <p className="text-xs text-slate-400 mt-0.5">{date}</p>
                            </div>
                            <div className="flex items-center gap-2 shrink-0 ml-3">
                              <span className={cn("text-xs font-bold px-2 py-0.5 rounded-full", statusColor)}>
                                {order.status === "PAID" ? "Paid" : order.status === "DISPUTED" ? "Disputed" : "Refunded"}
                              </span>
                              <span className="hidden sm:inline text-xs text-indigo-500 font-semibold">View receipt</span>
                              <ArrowRight className="w-3.5 h-3.5 text-indigo-400" />
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                <PurchaseReceiptModal order={receiptOrder} onClose={() => setReceiptOrder(null)} />

                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Transaction History</p>
                <CoinSpendTable
                  counterpartLabel="Name of the student"
                  page={coinSpendsPage}
                  isLoading={coinSpendsLoading}
                  isError={coinSpendsError}
                  searchTerm={searchTerm}
                  onSearchChange={(v) => { setSearchTerm(v); setPageIndex(0); }}
                  dateFrom={dateFrom}
                  dateTo={dateTo}
                  onDateFromChange={(d) => { setDateFrom(d); setPageIndex(0); }}
                  onDateToChange={(d) => { setDateTo(d); setPageIndex(0); }}
                  pageIndex={pageIndex}
                  onPageChange={setPageIndex}
                  onRowClick={(row) => { if (row.threadId) navigate(`/teacher-inquiries?thread=${row.threadId}`); }}
                  registrationDate={registrationDate}
                />
              </div>
            </main>
          </div>
        </div>
      </SidebarProvider>
    </div>
  );
}
